<script lang="ts">
  import { onMount } from "svelte"
  import { blankOrValue } from "$app/utils"
  import ToggleEdit from "$app/components/ToggleEdit.svelte"
  import Tiptap from "$app/components/Tiptap.svelte"
  import { onlyTextButtons } from "$app/components/Tiptap/buttons"
  import { FileText, FileLock, FileWarning, FileCheck2 } from "lucide-svelte"
  import { appStore } from "$app/store.svelte"

  type Props = {
    goal: GoalType
    onsave: (payload: Record<string, any>, field?: string) => void
  }

  let { goal, onsave }: Props = $props()

  const current_user = appStore.current_user
  const editable: boolean = appStore.editable || appStore.update_goals

  let description = $state(goal.description)
  let action_plan = $state(goal.action_plan)
  let obstacles_text = $state(goal.obstacles_text)
  let leadership_notes = $state(goal.leadership_notes)

  // Parse Action Plan to remove HTML Tags to see if it has actual text content
  let ap_html = $state("")
  let ap_div = document.createElement("div")

  let ap_text = ap_div.textContent || ap_div.innerText || ""
  let show_action_plan = ap_text.length > 0 || false

  onMount(() => {
    ap_html = action_plan
    ap_div.innerHTML = ap_html
  })
</script>

<div class="col-span-12 mb-4">
  <h3 class="flex items-center mb-1">
    <FileText class="text-gray-400 mr-1 size-5" />
    Description
  </h3>
  {#if editable}
    <ToggleEdit
      onblur={() => {
        onsave({ description: description }, "Description")
      }}
    >
      {#snippet editcont()}
        <div>
          <Tiptap
            id="edit-description"
            buttons={onlyTextButtons}
            content={goal.description}
            onupdate={({ html }) => (description = html)}
            showSaveArrow={true}
            onsave={() => document.body.click()}
          />
        </div>
      {/snippet}
      {#snippet showcont()}
        <div class="border px-4 py-2 shadow-md ProseMirror">
          {@html blankOrValue(description, "Enter a description of this goal")}
        </div>
      {/snippet}
    </ToggleEdit>
  {:else}
    <div
      class="border border-gray-200 bg-gray-100 py-2 px-4 h-24 overflow-y-auto text-sm ProseMirror"
    >
      {#if description && description.length > 0}
        {@html description}
      {:else}
        <span class="italic">Not Set</span>
      {/if}
    </div>
  {/if}
</div>

{#if current_user.role === "leader" || current_user.role === "admin"}
  <div class="col-span-12 mb-4">
    <h3 class="flex items-center mb-1">
      <FileLock class="text-gray-400 mr-1 size-5" />
      Leadership Notes
    </h3>
    <ToggleEdit
      onblur={() => {
        onsave({ leadership_notes: leadership_notes }, "Leadership Notes")
      }}
    >
      {#snippet editcont()}
        <div>
          <Tiptap
            id="edit-leadership-notes"
            buttons={onlyTextButtons}
            content={goal.leadership_notes}
            onupdate={({ html }) => (leadership_notes = html)}
            showSaveArrow={true}
            onsave={() => document.body.click()}
          />
        </div>
      {/snippet}
      {#snippet showcont()}
        <div class="border px-4 py-2 shadow-md ProseMirror">
          {@html blankOrValue(
            leadership_notes,
            "Enter any private notes here which are visible only for Leadership Team members."
          )}
        </div>
      {/snippet}
    </ToggleEdit>
  </div>
{/if}

{#if show_action_plan}
  <div class="col-span-12 mb-4">
    <h3 class="flex items-center mb-1">
      <FileCheck2 class="text-gray-400 mr-1 size-5" />
      Action Plan
    </h3>
    {#if editable}
      <ToggleEdit
        onblur={() => {
          onsave({ action_plan: action_plan }, "Action Plan")
        }}
      >
        {#snippet editcont()}
          <div>
            <Tiptap
              id="edit-action-plan"
              buttons={onlyTextButtons}
              content={goal.action_plan}
              onupdate={({ html }) => (action_plan = html)}
              showSaveArrow={true}
              onsave={() => document.body.click()}
            />
          </div>
        {/snippet}
        {#snippet showcont()}
          <div class="border px-4 py-2 shadow-md ProseMirror">
            {@html blankOrValue(
              action_plan,
              "Enter your action plan for accomplishing this goal"
            )}
          </div>
        {/snippet}
      </ToggleEdit>
    {:else}
      <div
        class="border border-gray-200 bg-gray-100 py-2 px-4 h-24 overflow-y-auto text-sm ProseMirror"
      >
        {#if action_plan && action_plan.length > 0}
          {@html action_plan}
        {:else}
          <span class="italic">Not Set</span>
        {/if}
      </div>
    {/if}
  </div>
{/if}

<div class="col-span-12">
  <h3 class="flex items-center mb-1">
    <FileWarning class="text-gray-400 mr-1 size-5" />
    Obstacles
  </h3>
  {#if editable}
    <ToggleEdit
      onblur={() => {
        onsave({ obstacles_text: obstacles_text }, "Obstacles")
      }}
    >
      {#snippet editcont()}
        <div class="w-full">
          <Tiptap
            id="edit-obstacles-text"
            buttons={onlyTextButtons}
            content={goal.obstacles_text}
            onupdate={({ html }) => (obstacles_text = html)}
            showSaveArrow={true}
            onsave={() => document.body.click()}
          />
        </div>
      {/snippet}
      {#snippet showcont()}
        <div class="border px-4 py-2 shadow-md ProseMirror w-full">
          {@html blankOrValue(
            obstacles_text,
            "Enter any obstacles to accomplishing this goal"
          )}
        </div>
      {/snippet}
    </ToggleEdit>
  {:else}
    <div
      class="border border-gray-200 bg-gray-100 py-2 px-4 h-24 overflow-y-auto text-sm ProseMirror"
    >
      {#if obstacles_text && obstacles_text.length > 0}
        {@html obstacles_text}
      {:else}
        <span class="italic">Not Set</span>
      {/if}
    </div>
  {/if}
</div>
